import React, { useState } from 'react';
import { Clock } from 'lucide-react';
import { services } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import Reveal from './motion/Reveal';

const Services = () => {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('bodyContouring');

  const categories = [
    { id: 'bodyContouring', label: 'Body Contouring & Sculpting', services: services.bodyContouring },
    { id: 'fajas', label: 'Fajas (Compression)', services: services.fajas },
    { id: 'beauty', label: 'Beauty Services', services: services.beauty },
    { id: 'nails', label: 'Nails', services: services.nails },
    { id: 'flyers', label: 'Flyers', services: services.flyers }
  ];

  const activeServices = categories.find(cat => cat.id === activeCategory)?.services || [];
  const isFlyers = activeCategory === 'flyers';

  return (
    <section id="services" className="py-24 bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Reveal className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className="text-4xl font-normal text-black">
            {t.services.title}
          </h2>
          <div className="w-16 h-px bg-black mx-auto"></div>
          <p className="text-lg text-gray-700">
            {t.services.subtitle}
          </p>
        </Reveal>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              data-testid={`services-tab-${category.id}`}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-3 text-sm font-normal transition-all duration-200 border-2 ${
                activeCategory === category.id
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-black border-gray-300 hover:border-black'
              }`}
              style={{ borderRadius: '0px' }}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Services/Flyers grid */}
        {isFlyers ? (
          /* Flyers Display - Organized by Format */
          <div className="space-y-16">
            {/* Square Flyers */}
            {activeServices.filter(f => f.orientation === 'square').length > 0 && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                  {activeServices.filter(f => f.orientation === 'square').map((flyer) => (
                    <div key={flyer.id} className="bg-white border-2 border-black overflow-hidden hover:shadow-lg transition-all duration-200">
                      <div className="aspect-square bg-gray-100 relative p-4">
                        <img 
                          src={`/images/flyers/${flyer.image}`}
                          alt={flyer.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="p-4 text-center border-t-2 border-gray-200">
                        <h4 className="text-sm font-medium text-black">{flyer.name}</h4>
                        <p className="text-xs text-gray-600 mt-1">{flyer.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Landscape Flyers */}
            {activeServices.filter(f => f.orientation === 'landscape').length > 0 && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
                  {activeServices.filter(f => f.orientation === 'landscape').map((flyer) => (
                    <div key={flyer.id} className="bg-white border-2 border-black overflow-hidden hover:shadow-lg transition-all duration-200">
                      <div className="aspect-[16/9] bg-gray-100 relative p-4">
                        <img 
                          src={`/images/flyers/${flyer.image}`}
                          alt={flyer.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="p-4 text-center border-t-2 border-gray-200">
                        <h4 className="text-sm font-medium text-black">{flyer.name}</h4>
                        <p className="text-xs text-gray-600 mt-1">{flyer.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Portrait Flyers */}
            {activeServices.filter(f => f.orientation === 'portrait').length > 0 && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {activeServices.filter(f => f.orientation === 'portrait').map((flyer) => (
                    <div key={flyer.id} className="bg-white border-2 border-black overflow-hidden hover:shadow-lg transition-all duration-200">
                      <div className="aspect-[9/16] bg-gray-100 relative p-4">
                        <img 
                          src={`/images/flyers/${flyer.image}`}
                          alt={flyer.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="p-4 text-center border-t-2 border-gray-200">
                        <h4 className="text-sm font-medium text-black">{flyer.name}</h4>
                        <p className="text-xs text-gray-600 mt-1">{flyer.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Regular Services Display */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {activeServices.map((service) => (
              <div 
                key={service.id}
                className="bg-white p-8 border-2 border-gray-200 hover:border-black hover:shadow-lg transition-all duration-200 group"
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <h3 className="text-xl font-normal text-black group-hover:text-gray-700 transition-colors duration-200">
                      {service.name}
                    </h3>
                  </div>

                  <p className="text-sm text-gray-700 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="flex items-center space-x-2 text-sm text-gray-600 pt-2">
                    <Clock size={16} />
                    <span>{service.duration}</span>
                  </div>

                  <button 
                    onClick={() => {
                      const element = document.getElementById('contact');
                      if (!element) return;
                      if (window.__lenis) {
                        window.__lenis.scrollTo(element, { offset: -80, duration: 1.4 });
                      } else {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="text-sm font-normal text-black hover:text-gray-600 transition-colors duration-200 inline-block pt-2"
                  >
                    Book Now →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
